import { Children, FC, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import Hammer from 'react-hammerjs'
import useWindowSize from 'shared/hooks/window-size'
import styled from 'styled-components'

const Container = styled.div`
    overflow: hidden;
    position: relative;
`

const Wrapper = styled.div`
    left: 0;
    position: absolute;
    top: 0;
    transition: transform 0.3s ease-in-out;
`

type Props = {
    height?: number
    width?: number
    onUpdate?: (index: number) => void
    children: React.ReactNode
}

const ScrollCarousel: FC<Props> = ({ children, height: targetHeight, width: targetWidth, onUpdate }) => {
    const { innerHeight, innerWidth } = useWindowSize()
    const childCount = Children.count(children)
    const height = targetHeight || innerHeight
    const width = targetWidth || innerWidth
    const wrapperRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<number[]>([])
    const [activeIndex, setActiveIndex] = useState<number>(0)

    const updateScroll = () => {
        if (!wrapperRef.current) return
        const scroll = activeIndex < scrollRef.current.length ? scrollRef.current[activeIndex] : 0
        wrapperRef.current.style.transform = `translate3d(0, ${-scroll}px, 0)`
    }

    const onPan = ({ deltaY }) => {
        if (!wrapperRef.current) return
        const scroll = activeIndex < scrollRef.current.length ? scrollRef.current[activeIndex] : 0
        wrapperRef.current.style.transform = `translate3d(0, ${-scroll + deltaY}px, 0)`
        wrapperRef.current.style.transition = 'none'
    }

    const onPanEnd = () => {
        if (!wrapperRef.current) return
        wrapperRef.current.style.transition = ''
        updateScroll()
    }

    const onSwipeUp = () => {
        if (!scrollRef.current.length) return
        const index = activeIndex || 0
        const nextIndex = Math.min(index + 1, scrollRef.current.length - 1)
        setActiveIndex(nextIndex)
    }

    const onSwipeDown = () => {
        if (!scrollRef.current.length) return
        const index = activeIndex || 0
        const nextIndex = Math.max(0, index - 1)
        setActiveIndex(nextIndex)
    }

    useEffect(() => {
        if (!wrapperRef.current) return

        let lastOffset = 0
        scrollRef.current = []
        for (let i = 0; i < wrapperRef.current.children.length; i++) {
            const el = wrapperRef.current.children[i]
            const height = el.scrollHeight
            scrollRef.current.push(lastOffset)
            lastOffset += height
        }
        updateScroll()
    }, [wrapperRef, width, childCount])

    useEffect(() => {
        updateScroll()
        onUpdate && onUpdate(activeIndex)
    }, [activeIndex, onUpdate])

    return (
        <Hammer
            direction="DIRECTION_VERTICAL"
            options={{ preventDefault: true, touchAction: 'swipe-y' }}
            onPan={onPan}
            onPanEnd={onPanEnd}
            onSwipeUp={onSwipeUp}
            onSwipeDown={onSwipeDown}
        >
            <Container style={{ height, width }}>
                <Wrapper ref={wrapperRef}>{children}</Wrapper>
            </Container>
        </Hammer>
    )
}

export default ScrollCarousel
